// Full-parity AI makeover (backend-owns-results). The client creates the job with
// client-generated session/cycle ids + source context, then streams renders for a
// LIVE preview while the AWS worker finalizes the makeover into the sync graph.
// The client never downloads or uploads render bytes — it just pulls the finished
// SESSION/CYCLE from sync afterward. Port of RedecorateSession's drive loop.

import { createJob, getJob } from "./apiClient";
import type { AssetRef, Job } from "./types";

export interface LiveRender {
  renderKey: string;
  concept: number;
  sourceIndex: number;
  url: string; // presigned GET from the latest poll (live display only)
}

export interface MakeoverProgress {
  status: Job["status"];
  total: number;
  done: number;
  concepts: { index: number; name: string | null; brief: string | null }[];
  styleBrief: string | null;
  renders: LiveRender[];
}

const POLL_MS = 3000;
const MAX_POLL_MS = 16 * 60 * 1000;

function parseRenderKey(key: string): { concept: number; sourceIndex: number } {
  let m = key.match(/\/(\d+)\/(\d+)\.png$/);
  if (m) return { concept: Number(m[1]), sourceIndex: Number(m[2]) };
  m = key.match(/\/(\d+)\.png$/);
  if (m) return { concept: 0, sourceIndex: Number(m[1]) };
  return { concept: 0, sourceIndex: 0 };
}

export interface StartArgs {
  room: { id: string; name: string };
  goal: string;
  /** Aligned: sourceKeys[i] is the S3 key, sourceFileNames[i] the local <uuid>.jpg. */
  sourceKeys: string[];
  sourceFileNames: string[];
  /** Source photo AssetRefs (passed to the worker for the cycle's before-photos). */
  sourceAssets: AssetRef[];
  feedback?: string;
  previousJobId?: string;
  cycleIndex?: number;
  /** Client-generated ids; the backend finalizes the makeover onto these. */
  sessionId: string;
  cycleId: string;
}

/**
 * Create the job (handing the worker everything it needs to finalize) and poll
 * until it terminates, streaming renders for live display. Returns the final job.
 */
export async function runJob(
  args: StartArgs,
  onProgress: (p: MakeoverProgress) => void,
  signal?: { aborted: boolean },
): Promise<{ job: Job; renders: LiveRender[] }> {
  const { jobId } = await createJob({
    room: args.room,
    goal: args.goal,
    sourceKeys: args.sourceKeys,
    feedback: args.feedback,
    previousJobId: args.previousJobId,
    // Backend-owns-results: the worker finalizes the SESSION/CYCLE into sync.
    sessionId: args.sessionId,
    cycleId: args.cycleId,
    cycleIndex: args.cycleIndex,
    sourceFileNames: args.sourceFileNames,
    sourceAssets: args.sourceAssets,
  });

  const renders = new Map<string, LiveRender>();
  const started = Date.now();
  let job: Job;

  for (;;) {
    if (signal?.aborted) throw new Error("cancelled");
    job = await getJob(jobId);

    for (const r of job.results) {
      const meta =
        r.concept != null && r.sourceIndex != null
          ? { concept: r.concept, sourceIndex: r.sourceIndex }
          : parseRenderKey(r.renderKey);
      // Always refresh the url (it's re-signed each poll).
      renders.set(r.renderKey, {
        renderKey: r.renderKey,
        concept: meta.concept,
        sourceIndex: meta.sourceIndex,
        url: r.url,
      });
    }

    onProgress({
      status: job.status,
      total: job.progress?.total ?? 0,
      done: job.progress?.done ?? 0,
      concepts: job.concepts ?? [],
      styleBrief: job.styleBrief ?? null,
      renders: [...renders.values()],
    });

    if (job.status === "completed" || job.status === "failed") break;
    if (Date.now() - started > MAX_POLL_MS) break;
    await sleep(POLL_MS);
  }

  return { job, renders: [...renders.values()] };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
